import * as React from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SyncIcon from '@mui/icons-material/Sync'
import AddIcon from '@mui/icons-material/Add'
import competitorService from '../../services/competitor'

function CompetitorSettings() {
  const [competitors, setCompetitors] = React.useState([])
  const [ticker, setTicker] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [syncing, setSyncing] = React.useState(null)   // id of row being synced
  const [error, setError] = React.useState(null)
  const [success, setSuccess] = React.useState(null)

  const clearMessages = () => {
    setError(null)
    setSuccess(null)
  }

  const fetchList = async () => {
    try {
      const res = await competitorService.listCompetitors()
      setCompetitors(res.data)
    } catch {
      setError('Failed to load competitors list')
    }
  }

  React.useEffect(() => {
    fetchList()
  }, [])

  const handleAdd = async () => {
    if (!ticker.trim()) return
    clearMessages()
    setLoading(true)
    try {
      await competitorService.addCompetitor(ticker.trim().toUpperCase())
      setTicker('')
      setSuccess(`Competitor added and EDGAR data synced.`)
      await fetchList()
    } catch (err) {
      const detail = err?.response?.data?.detail
      setError(detail || 'Failed to add competitor')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    clearMessages()
    try {
      await competitorService.removeCompetitor(id)
      setCompetitors((prev) => prev.filter((c) => c.id !== id))
    } catch {
      setError('Failed to remove competitor')
    }
  }

  const handleSync = async (id) => {
    clearMessages()
    setSyncing(id)
    try {
      const res = await competitorService.syncCompetitor(id)
      setSuccess(`Synced ${res.data.ticker}: ${res.data.rows_synced} records updated`)
      await fetchList()
    } catch (err) {
      const detail = err?.response?.data?.detail
      setError(detail || 'Sync failed')
    } finally {
      setSyncing(null)
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString()
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Competitor Tracking
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Add competitor stock tickers. The system will pull their SEC EDGAR filings
        so you can run financial comparisons from the chat.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Add competitor */}
      <Box display="flex" gap={1} mb={4} alignItems="center">
        <TextField
          label="Ticker symbol"
          placeholder="e.g. AAPL"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          size="small"
          sx={{ width: 200 }}
          inputProps={{ maxLength: 10 }}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          onClick={handleAdd}
          disabled={loading || !ticker.trim()}
          sx={{ textTransform: 'none' }}
        >
          Add Competitor
        </Button>
      </Box>

      {/* Competitors table */}
      {competitors.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No competitors added yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Ticker</strong></TableCell>
                <TableCell><strong>Company</strong></TableCell>
                <TableCell><strong>CIK</strong></TableCell>
                <TableCell><strong>Last Synced</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {competitors.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Chip label={c.ticker} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>{c.company_name || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{c.cik}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {formatDate(c.last_synced)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="Re-sync EDGAR data"
                      onClick={() => handleSync(c.id)}
                      disabled={syncing === c.id}
                    >
                      {syncing === c.id
                        ? <CircularProgress size={16} />
                        : <SyncIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Remove"
                      onClick={() => handleDelete(c.id)}
                      sx={{ color: 'error.main', ml: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default CompetitorSettings
